import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  setAddedMaps,
  setRelatedToVisibleMaps,
  setVisibleMapsDependencies,
  setRelativesViewMode,
  setObjectiveDisplayMode,
  toggleMapDependencies,
  enableShowAllRelatives,
  ObjectiveDisplayMode,
} from '../../store/map3UISlice';
import { Switch } from '../../components_catalyst/switch';
import { DropdownMenu, DropdownDivider } from '../../components_catalyst/dropdown';
import { BadgeButton } from '../../components_catalyst/badge';
import { getDependencyId } from '../utils/dependencyUtils';
import { getAccessibleMaps } from '../../utils/getAccessibleMapsUtil';
import { useComprehensiveDependencies } from '../hooks/useComprehensiveDependencies';
import { DependencyIconState } from './DualIconContainer';
import { Disclosure } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { MapIcon } from '@heroicons/react/20/solid';
import { DependenciesIcon } from '../../components_misc/DependenciesIcon';
import { useParams } from 'react-router-dom';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { DualIconContainer } from './DualIconContainer';
import { useAllRelativesData } from '../hooks/useAllRelativesData';
import { loadMapDataAsync } from '../../store/map3DataSlice';
import { selectMultipleMapsDependencyData } from '../../store/selectors/mapDependencySelectors';

/**
 * Component prop interfaces for the MapOptionsDropDownMenu system.
 * These interfaces define the contract between the main component and its subcomponents.
 */

// Types for subcomponent props
interface DropdownInputFormProps {
  isAddMode: boolean;
  searchText: string;
  onModeChange: (addMode: boolean) => void;
  onSearchTextChange: (text: string) => void;
  accessibleMaps: any[];
  relatedMapUuids: Set<string>;
  onAddMap: (map: any) => void;
  disabled: boolean;
}

interface AddMapInputFieldProps {
  searchText: string;
  accessibleMaps: any[];
  relatedMapUuids: Set<string>;
  onSearchTextChange: (text: string) => void;
  onAddMap: (map: any) => void;
  disabled: boolean;
}

interface FilterInputFieldProps {
  searchText: string;
  onSearchTextChange: (text: string) => void;
  disabled: boolean;
}

interface DropdownListProps {
  isAddMode: boolean;
  addedMaps: any[];
  relatedToVisibleMaps: any[];
  dependencies: any[];
  onToggleMap: (uuid: string) => void;
  onToggleDependency: (dep: any) => void;
  onToggleMapDependencies?: (mapUuid: string) => void;
  onBulkUpdateDependencies?: (deps: Array<{ depId: string; visible: boolean }>) => void;
  searchText: string;
  disabled: boolean;
  loadingMapUuids: Set<string>;
}

/**
 * AddMapInputField: Provides autocomplete search functionality for adding new maps.
 * Features keyboard navigation, portal-based dropdown positioning, and focus management.
 */
function AddMapInputField(props: AddMapInputFieldProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Filter accessible maps (not already related) by search text
  const possibleMapsToAdd = useMemo(() => {
    const maps = props.accessibleMaps
      .filter((map) => !props.relatedMapUuids.has(map.uuid))
      .filter((map) => map.title.toLowerCase().includes(props.searchText.toLowerCase()))
      .sort((a, b) => a.title.localeCompare(b.title));
    return maps;
  }, [props.accessibleMaps, props.relatedMapUuids, props.searchText]);

  // Show dropdown if there are results and input is focused
  useEffect(() => {
    if (props.searchText && possibleMapsToAdd.length > 0) {
      setShowDropdown(true);
      setActiveIndex(0);
    } else {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  }, [props.searchText, possibleMapsToAdd.length]);

  // Position the dropdown absolutely below the input using a portal
  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'absolute',
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [showDropdown, props.searchText]);

  // Reposition on window resize/scroll
  useEffect(() => {
    if (!showDropdown) return;
    const handle = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownStyle({
          position: 'absolute',
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          zIndex: 9999,
        });
      }
    };
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [showDropdown]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || possibleMapsToAdd.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % possibleMapsToAdd.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + possibleMapsToAdd.length) % possibleMapsToAdd.length);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      props.onAddMap(possibleMapsToAdd[activeIndex]);
      setShowDropdown(false);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Focus management: only hide dropdown if focus leaves both input and dropdown
  const handleBlur = (_e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (
        !inputRef.current?.contains(document.activeElement) &&
        !dropdownRef.current?.contains(document.activeElement)
      ) {
        setShowDropdown(false);
      }
    }, 100);
  };

  // The dropdown menu rendered in a portal
  const dropdown =
    showDropdown && possibleMapsToAdd.length > 0
      ? ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            id="addable-maps-list"
            style={dropdownStyle}
            className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
            tabIndex={-1}
          >
            {possibleMapsToAdd.map((milestoneMap, idx) => (
              <button
                key={milestoneMap.uuid}
                id={`addable-map-${milestoneMap.uuid}`}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  props.onAddMap(milestoneMap);
                  setShowDropdown(false);
                }}
                className={`w-full text-left px-4 py-2 flex items-center gap-2 ${
                  idx === activeIndex ? 'hover:bg-blue-50' : 'hover:bg-gray-100'
                }`}
                tabIndex={-1}
              >
                {milestoneMap.title}
              </button>
            ))}
            {possibleMapsToAdd.length === 0 && (
              <div className="px-4 py-2 text-sm text-gray-500">No maps found to add</div>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={props.searchText}
          onChange={(e) => props.onSearchTextChange(e.target.value)}
          placeholder="Search maps to add..."
          className="w-full pl-3 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={props.disabled}
          onFocus={() => {
            if (props.searchText && possibleMapsToAdd.length > 0) setShowDropdown(true);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-activedescendant={
            showDropdown && activeIndex >= 0 && possibleMapsToAdd[activeIndex]
              ? `addable-map-${possibleMapsToAdd[activeIndex].uuid}`
              : undefined
          }
          aria-autocomplete="list"
          aria-controls="addable-maps-list"
          autoComplete="off"
        />
      </div>
      {dropdown}
    </>
  );
}

/**
 * FilterInputField: Simple text input for filtering existing maps and dependencies.
 * Used when not in add mode to search through current items.
 */
function FilterInputField(props: FilterInputFieldProps) {
  return (
    <input
      type="text"
      value={props.searchText}
      onChange={(e) => props.onSearchTextChange(e.target.value)}
      placeholder="Filter maps and dependencies..."
      className="w-full pl-3 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      disabled={props.disabled}
    />
  );
}

/**
 * DropdownInputForm: Header component that handles view mode selection and input switching.
 * Renders view mode badges (Stacked/Shared Timeline), add/filter toggle, and appropriate input field.
 */
function DropdownInputForm(props: DropdownInputFormProps) {
  const dispatch = useDispatch<AppDispatch>();
  const viewMode = useSelector((state: RootState) => state.map3UI.mapControls.relatives.viewMode);

  const selectedBadge = 'border-2 border-slate-400';
  const notSelectedBadge = '';

  const handleViewModeChange = useCallback(
    (newMode: 'stacked' | 'sharedTimeline') => {
      dispatch(setRelativesViewMode(newMode));
    },
    [dispatch],
  );

  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <BadgeButton
            color="zinc"
            onClick={() => handleViewModeChange('stacked')}
            className={`cursor-pointer ${viewMode === 'stacked' ? selectedBadge : notSelectedBadge}`}
          >
            Stacked
          </BadgeButton>
          <BadgeButton
            color="zinc"
            onClick={() => handleViewModeChange('sharedTimeline')}
            className={`cursor-pointer ${viewMode === 'sharedTimeline' ? selectedBadge : notSelectedBadge}`}
          >
            Shared Timeline
          </BadgeButton>
        </div>
        <div className="flex-shrink-0 pr-1">
          <Switch
            checked={props.isAddMode}
            onChange={props.onModeChange}
            color="blue"
            aria-label={props.isAddMode ? 'Switch to filter mode' : 'Switch to add mode'}
            disabled={props.disabled}
          />
        </div>
      </div>
      {props.isAddMode ? (
        <AddMapInputField
          searchText={props.searchText}
          accessibleMaps={props.accessibleMaps}
          relatedMapUuids={props.relatedMapUuids}
          onSearchTextChange={props.onSearchTextChange}
          onAddMap={props.onAddMap}
          disabled={props.disabled}
        />
      ) : (
        <FilterInputField
          searchText={props.searchText}
          onSearchTextChange={props.onSearchTextChange}
          disabled={props.disabled}
        />
      )}
    </div>
  );
}

/**
 * Shared helper hooks and utility functions for map operations.
 * These hooks provide reusable logic across multiple components.
 */

/**
 * useMapHelpers: Provides map visibility checking and title resolution.
 * Centralizes common map operations to reduce code duplication.
 */
function useMapHelpers() {
  // Get all maps from Redux to resolve titles
  const allMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);

  // Helper to check if a map is being shown
  function isMapVisible(mapUuid: string, addedMaps: any[], relatedToVisibleMaps: any[]): boolean {
    const allMapOptions = [...addedMaps, ...relatedToVisibleMaps];
    const mapOption = allMapOptions.find((m) => m.mapUuid === mapUuid);
    return mapOption?.visible || false;
  }

  // Helps to get map titles for display
  const getMapTitle = (mapUuid: string): string => {
    const map = allMaps.find((m) => m.uuid === mapUuid);
    return map?.title || '';
  };

  return { isMapVisible, getMapTitle };
}

/**
 * useDependencyHelpers: Provides dependency visibility checking and state access.
 * Wraps Redux selectors for dependency-related operations.
 */
function useDependencyHelpers() {
  // Get Redux state for dependency visibility
  const dependenciesVisibilityState = useSelector(
    (state: RootState) => state.map3UI.mapControls.relatives.visibleMapsDependencies,
  );

  // Helper to check if a dependency is being shown
  function isDependencyVisible(depId: string): boolean {
    return dependenciesVisibilityState.find((d) => d.depId === depId)?.visible || false;
  }

  return { isDependencyVisible, dependenciesVisibilityState };
}

/**
 * AddedMapsSection: Displays user-added maps with visibility toggles.
 * Shows maps that were manually added through the search interface.
 * Includes search filtering and empty state handling.
 */
function AddedMapsSection({
  addedMaps,
  isAddMode,
  searchText,
  onToggleMap,
}: {
  addedMaps: any[];
  isAddMode: boolean;
  searchText: string;
  onToggleMap: (uuid: string) => void;
}) {
  const { isMapVisible, getMapTitle } = useMapHelpers();

  // Get added maps with titles for display, filtered by search text
  const addedMapsForDisplay = addedMaps
    .map((addedMapState) => {
      return { ...addedMapState, title: getMapTitle(addedMapState.mapUuid) };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .filter((item) => !searchText || item.title.toLowerCase().includes(searchText.toLowerCase()));

  return (
    <>
      {/* Added Maps Section - only show if in add mode or if there are added maps */}
      {(isAddMode || addedMaps.length > 0) && (
        <>
          <div
            className="px-3 py-2 w-full"
            style={{ minWidth: 320, width: '100%', maxWidth: '100%' }}
          >
            <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5">
                <MapIcon className="w-4 h-4" />
              </span>
              Added Maps
            </h3>
            {addedMapsForDisplay.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {isAddMode
                  ? 'No added maps yet. Add one using the search above.'
                  : searchText
                    ? 'No added maps found'
                    : 'No added maps'}
              </div>
            ) : (
              <div>
                {addedMapsForDisplay.map((map) => {
                  const visible = isMapVisible(map.mapUuid, addedMaps, []);
                  return (
                    <button
                      key={map.mapUuid}
                      type="button"
                      onClick={() => onToggleMap(map.mapUuid)}
                      className={`w-full text-left px-4 py-2 rounded cursor-pointer ${
                        visible ? 'font-extrabold hover:bg-gray-100' : 'hover:bg-gray-100'
                      }`}
                      aria-pressed={!!visible}
                    >
                      {map.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Divider after added maps only if we don't have Map Display coming next */}
          {isAddMode && <DropdownDivider />}
        </>
      )}
    </>
  );
}

/**
 * MapDisplaySection: Collapsible section containing objective display mode controls.
 * Only visible in filter mode. Contains radio buttons for controlling how objectives are displayed.
 * Uses Disclosure pattern for expand/collapse functionality.
 */
function MapDisplaySection({ isAddMode, addedMaps }: { isAddMode: boolean; addedMaps: any[] }) {
  return (
    <>
      {/* Map Display Controls Section: only in filter mode, appears right after Added Maps */}
      {!isAddMode && (
        <>
          {/* Divider before Map Display - only if there were Added Maps above */}
          {addedMaps.length > 0 && <DropdownDivider />}
          <div
            className="px-3 py-2 w-full"
            style={{ minWidth: 320, width: '100%', maxWidth: '100%' }}
          >
            <Disclosure defaultOpen={false}>
              {({ open }) => (
                <>
                  <Disclosure.Button className="w-full flex items-center justify-between text-left text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-sm px-2 py-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5">
                        <AdjustmentsHorizontalIcon className="w-4 h-4" />
                      </span>
                      <span>Map Display</span>
                    </div>
                    {open ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                  </Disclosure.Button>

                  <Disclosure.Panel className="overflow-hidden">
                    <div className="transition-all duration-200 ease-in-out">
                      <div className="mt-2 px-4 py-2 space-y-3">
                        <ObjectiveDisplayModeRadioGroup />
                      </div>
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
          {/* Divider after Map Display, before Related Maps */}
          <DropdownDivider />
        </>
      )}
    </>
  );
}

/**
 * RelatedMapsSection: Displays automatically discovered related maps with dual-icon controls.
 * Handles map visibility toggles and dependency bulk operations.
 * Includes search filtering and uses Redux selectors for efficient dependency state management.
 */
function RelatedMapsSection({
  relatedToVisibleMaps,
  addedMaps,
  dependencies,
  isAddMode,
  searchText,
  disabled,
  onToggleMap,
  onToggleMapDependencies,
}: {
  relatedToVisibleMaps: any[];
  addedMaps: any[];
  dependencies: any[];
  isAddMode: boolean;
  searchText: string;
  disabled: boolean;
  onToggleMap: (uuid: string) => void;
  onToggleMapDependencies?: (mapUuid: string) => void;
}) {
  const { isMapVisible, getMapTitle } = useMapHelpers();

  // Get related maps with titles for display, filtered by search text
  const relatedMapsForDisplay = relatedToVisibleMaps
    .map((relatedMapState) => {
      return { ...relatedMapState, title: getMapTitle(relatedMapState.mapUuid) };
    })
    // Apply filter if in filter mode and in use.
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .filter((item) => !searchText || item.title.toLowerCase().includes(searchText.toLowerCase()));

  // Redux-based bulk dependency data for all related maps
  const mapUuids = relatedMapsForDisplay.map((map) => map.mapUuid);
  const bulkDependencyData = useSelector((state: RootState) =>
    selectMultipleMapsDependencyData(state, dependencies, mapUuids),
  );

  return (
    <>
      {/* Related Maps Section */}
      <div className="px-3 py-2 w-full" style={{ minWidth: 320, width: '100%', maxWidth: '100%' }}>
        <Disclosure defaultOpen={false}>
          {({ open }) => (
            <>
              <Disclosure.Button className="w-full flex items-center justify-between text-left text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-sm px-2 py-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5">
                    <MapIcon className="w-4 h-4" />
                  </span>
                  <span>Related Maps</span>
                </div>
                {open ? (
                  <ChevronDownIcon className="w-4 h-4" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4" />
                )}
              </Disclosure.Button>

              <Disclosure.Panel className="overflow-hidden">
                <div className="transition-all duration-200 ease-in-out">
                  <div className="mt-2">
                    {relatedMapsForDisplay.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        {isAddMode
                          ? 'No related maps yet. Add one using the search above.'
                          : searchText
                            ? 'No related maps found'
                            : 'No related maps'}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {relatedMapsForDisplay.map((mapState) => {
                          const visible = isMapVisible(
                            mapState.mapUuid,
                            addedMaps,
                            relatedToVisibleMaps,
                          );
                          const mapData = bulkDependencyData[mapState.mapUuid];
                          if (!mapData) return null; // Safety check

                          const { dependencyState, dependencyCount, visibleDependencyCount } =
                            mapData;

                          return (
                            <div
                              key={mapState.mapUuid}
                              className={`
                                flex items-center justify-between
                                px-4 py-2 
                                rounded-lg
                                transition-colors duration-150
                                hover:bg-gray-50
                                ${visible ? 'bg-gray-100' : ''}
                              `}
                            >
                              {/* Map Title */}
                              <div className="flex-1 min-w-0">
                                <span
                                  className={`
                                      text-sm truncate block
                                      ${visible ? 'font-semibold text-gray-900' : 'text-gray-700'}
                                    `}
                                  title={mapState.title}
                                >
                                  {mapState.title}
                                </span>
                              </div>

                              {/* Dual Icon Container */}
                              <DualIconContainer
                                isMapVisible={visible}
                                onVisibilityToggle={() => onToggleMap(mapState.mapUuid)}
                                dependencyState={dependencyState}
                                onDependencyToggle={() =>
                                  onToggleMapDependencies?.(mapState.mapUuid)
                                }
                                mapTitle={mapState.title}
                                dependencyCount={dependencyCount}
                                visibleDependencyCount={visibleDependencyCount}
                                disabled={disabled}
                                className="flex-shrink-0 ml-3"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
    </>
  );
}

/**
 * DependenciesSection: Collapsible list of individual dependencies with bulk selection.
 * Provides granular control over dependency visibility with "Select All" functionality.
 * Only visible in filter mode, includes search filtering and checkbox interactions.
 */
function DependenciesSection({
  dependencies,
  isAddMode,
  searchText,
  onToggleDependency,
  onBulkUpdateDependencies,
}: {
  dependencies: any[];
  isAddMode: boolean;
  searchText: string;
  onToggleDependency?: (dep: any) => void;
  onBulkUpdateDependencies?: (deps: Array<{ depId: string; visible: boolean }>) => void;
}) {
  const { isDependencyVisible, dependenciesVisibilityState } = useDependencyHelpers();

  return (
    <>
      {/* Divider before Dependencies section, only in filter mode */}
      {!isAddMode && <DropdownDivider />}

      {/* Collapsible Dependencies Section: only in filter mode */}
      {!isAddMode && (
        <div
          className="px-3 py-2 w-full"
          style={{ minWidth: 320, width: '100%', maxWidth: '100%' }}
        >
          <Disclosure defaultOpen={false}>
            {({ open }) => {
              // Calculate dependency count indicators
              const totalDependencies = dependencies.length;
              const visibleDependencies = dependencies.filter((dep) => {
                const depId = getDependencyId(dep);
                return isDependencyVisible(depId);
              }).length;

              return (
                <>
                  <Disclosure.Button className="w-full flex items-center justify-between text-left text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-sm px-2 py-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5">
                        <DependenciesIcon className="w-4 h-4" />
                      </span>
                      <span>
                        Dependencies ({visibleDependencies}/{totalDependencies})
                      </span>
                    </div>
                    {open ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                  </Disclosure.Button>

                  <Disclosure.Panel className="overflow-hidden">
                    <div className="transition-all duration-200 ease-in-out">
                      {dependencies.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          {searchText ? 'No dependencies found' : 'No dependencies exist'}
                        </div>
                      ) : (
                        <div className="mt-2">
                          {/* Bulk Control - Select All */}
                          <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-200">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={
                                  visibleDependencies === totalDependencies && totalDependencies > 0
                                }
                                onChange={(e) => {
                                  const shouldSelectAll = e.target.checked;

                                  // Batch update - all or nothing operation
                                  if (onBulkUpdateDependencies) {
                                    // Build new visibility state for all dependencies at once
                                    const updatedDependencies = dependenciesVisibilityState.map(
                                      (depState) => {
                                        // Check if this dependency is in our current filtered list
                                        const isInFilteredList = dependencies.some(
                                          (dep) => getDependencyId(dep) === depState.depId,
                                        );

                                        if (isInFilteredList) {
                                          // Update visibility for dependencies in the filtered list
                                          return { ...depState, visible: shouldSelectAll };
                                        } else {
                                          // Keep unchanged for dependencies not in the filtered list
                                          return depState;
                                        }
                                      },
                                    );

                                    // Single batch update to Redux
                                    onBulkUpdateDependencies(updatedDependencies);
                                  }
                                }}
                                ref={(el) => {
                                  if (el) {
                                    // Remove indeterminate state - make it a simple on/off
                                    el.indeterminate = false;
                                  }
                                }}
                              />
                              <span className="text-sm font-medium text-gray-700">Select All</span>
                            </label>
                          </div>

                          {/* Individual dependency list */}
                          <div className="divide-y divide-gray-100">
                            {dependencies.map((dep) => {
                              const depId = getDependencyId(dep);
                              const visible = isDependencyVisible(depId);
                              return (
                                <button
                                  key={depId}
                                  type="button"
                                  onClick={() => onToggleDependency?.(dep)}
                                  className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
                                    visible ? 'font-semibold text-gray-900' : 'text-gray-600'
                                  }`}
                                  aria-pressed={!!visible}
                                >
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      checked={visible}
                                      onChange={() => {}} // Handled by parent button click
                                      onClick={(e) => e.stopPropagation()} // Prevent double toggle
                                    />
                                    <span className="text-sm">
                                      {dep.sourceResult.text} → {dep.targetResult.text}
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </Disclosure.Panel>
                </>
              );
            }}
          </Disclosure>
        </div>
      )}
    </>
  );
}

/**
 * DropdownList: Orchestrator component that renders all sections in proper order.
 * Simplified after refactoring - now just coordinates the display of subsections.
 * Maintains proper section ordering and prop passing to child components.
 */
function DropdownList(props: DropdownListProps) {
  const { addedMaps, relatedToVisibleMaps, dependencies } = props;

  return (
    <>
      <AddedMapsSection
        addedMaps={addedMaps}
        isAddMode={props.isAddMode}
        searchText={props.searchText}
        onToggleMap={props.onToggleMap}
      />

      <MapDisplaySection isAddMode={props.isAddMode} addedMaps={addedMaps} />

      <RelatedMapsSection
        relatedToVisibleMaps={relatedToVisibleMaps}
        addedMaps={addedMaps}
        dependencies={dependencies}
        isAddMode={props.isAddMode}
        searchText={props.searchText}
        disabled={props.disabled}
        onToggleMap={props.onToggleMap}
        onToggleMapDependencies={props.onToggleMapDependencies}
      />

      <DependenciesSection
        dependencies={dependencies}
        isAddMode={props.isAddMode}
        searchText={props.searchText}
        onToggleDependency={props.onToggleDependency}
        onBulkUpdateDependencies={props.onBulkUpdateDependencies}
      />
    </>
  );
}

/**
 * ObjectiveDisplayModeRadioGroup: Radio button group for objective display mode selection.
 * Handles the three display modes: show_all, show_with_dependencies, show_all_relatives.
 * Uses atomic Redux actions to prevent race conditions when switching modes.
 */
function ObjectiveDisplayModeRadioGroup() {
  const dispatch = useDispatch<AppDispatch>();
  const currentMode = useSelector(
    (state: RootState) => state.map3UI.mapControls.relatives.objectiveDisplayMode,
  );
  const relativesSettings = useSelector((state: RootState) => state.map3UI.mapControls.relatives);

  const handleModeChange = useCallback(
    (mode: ObjectiveDisplayMode) => {
      if (mode === 'show_all_relatives') {
        // Use atomic action to prevent race condition
        dispatch(
          enableShowAllRelatives({
            relatedMaps: relativesSettings.relatedToVisibleMaps,
            dependencies: relativesSettings.visibleMapsDependencies,
          }),
        );
      } else {
        dispatch(setObjectiveDisplayMode(mode));
      }
    },
    [dispatch, relativesSettings.relatedToVisibleMaps, relativesSettings.visibleMapsDependencies],
  );

  const radioOptions = [
    {
      value: 'show_all' as ObjectiveDisplayMode,
      label: 'Show all objectives/rows',
      description: 'Display all objectives regardless of dependencies',
    },
    {
      value: 'show_with_dependencies' as ObjectiveDisplayMode,
      label: 'Show objectives with dependencies',
      description: 'Show only objectives that have visible dependencies',
    },
    {
      value: 'show_all_relatives' as ObjectiveDisplayMode,
      label: 'Show relatives for all visible dependencies',
      description: 'Auto-enable all related maps and dependencies',
    },
  ];

  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-medium text-gray-700 uppercase tracking-wide">
        Objective Display Mode
      </legend>
      <div className="space-y-2" role="radiogroup" aria-labelledby="objective-display-mode-legend">
        {radioOptions.map((option) => (
          <label
            key={option.value}
            className="flex items-start gap-3 cursor-pointer group hover:bg-gray-50 -mx-2 px-2 py-1 rounded-md transition-colors"
          >
            <input
              type="radio"
              name="objectiveDisplayMode"
              value={option.value}
              checked={currentMode === option.value}
              onChange={() => handleModeChange(option.value)}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
              aria-describedby={`${option.value}-description`}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                {option.label}
              </div>
              <div id={`${option.value}-description`} className="text-xs text-gray-500 mt-0.5">
                {option.description}
              </div>
            </div>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * MapOptionsDropDownMenu: Main dropdown component for managing map visibility and dependencies.
 *
 * Features:
 * - Add/remove maps through search interface
 * - Toggle map and dependency visibility
 * - Switch between view modes (Stacked/Shared Timeline)
 * - Control objective display modes with race condition prevention
 * - Bulk dependency operations with "Select All" functionality
 *
 * Architecture:
 * - Refactored into smaller, focused subcomponents for maintainability
 * - Uses Redux for state management with atomic actions to prevent race conditions
 * - Implements proper React patterns with useCallback/useEffect dependency management
 */
export function MapOptionsDropDownMenu(props: { disabled: boolean }) {
  const dispatch = useDispatch<AppDispatch>();
  const [isAddMode, setIsAddMode] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingMapUuids, setLoadingMapUuids] = useState<Set<string>>(new Set());

  // Get mapShortId from params
  const { mapShortId } = useParams();

  // Use the relatives data hook
  const mainMapUuid = useSelector(
    (state: RootState) =>
      state.map3Data.milestoneMaps.find((m) => getShortIdFromUuid(m.uuid) === mapShortId)?.uuid,
  );
  // TODO: Determine if this should be replaced with Redux state.
  const { allRelatedMilestoneMaps } = useAllRelativesData(mainMapUuid || '');

  // Get comprehensive dependency data using centralized hook
  const { allRelatedMapDependencies, relevantDependencies } =
    useComprehensiveDependencies(allRelatedMilestoneMaps);

  // Redux selectors (moved up to avoid hoisting issues)
  const relativesViewSettings = useSelector(
    (state: RootState) => state.map3UI.mapControls.relatives,
  );

  // Handle objective display mode changes
  useEffect(() => {
    if (!isInitialized || !mainMapUuid || !allRelatedMapDependencies.length) return;

    // Note: "show_all_relatives" mode auto-enable logic is now handled atomically
    // in the handleModeChange callback via enableShowAllRelatives action
  }, [
    relativesViewSettings.objectiveDisplayMode,
    isInitialized,
    mainMapUuid,
    allRelatedMapDependencies.length,
    dispatch,
    relativesViewSettings,
  ]);

  // Redux-based dependency management
  const handleToggleMapDependencies = useCallback(
    (mapUuid: string) => {
      // Calculate current state to determine target visibility
      const mapDependencies = relevantDependencies.filter(
        (dep) => dep.sourceMap.uuid === mapUuid || dep.targetMap.uuid === mapUuid,
      );

      if (mapDependencies.length === 0) return;

      const visibleCount = mapDependencies.filter((dep) => {
        const depId = getDependencyId(dep);
        return (
          relativesViewSettings.visibleMapsDependencies.find((d) => d.depId === depId)?.visible ||
          false
        );
      }).length;

      let currentState: DependencyIconState;
      if (visibleCount === 0) {
        currentState = 'none-visible';
      } else if (visibleCount === mapDependencies.length) {
        currentState = 'all-visible';
      } else {
        currentState = 'mixed';
      }

      const targetVisible = currentState === 'none-visible' || currentState === 'mixed';

      // Check if we need to auto-downgrade from "show_all_relatives" mode
      if (relativesViewSettings.objectiveDisplayMode === 'show_all_relatives' && !targetVisible) {
        // If user is hiding dependencies, downgrade to "show_with_dependencies"
        dispatch(setObjectiveDisplayMode('show_with_dependencies'));
      }

      // Simple MVP error handling
      try {
        dispatch(
          toggleMapDependencies({
            mapUuid,
            dependencies: relevantDependencies,
            targetVisible,
          }),
        );
      } catch (error) {
        console.error('Failed to toggle map dependencies:', error);
      }
    },
    [
      relevantDependencies,
      relativesViewSettings.visibleMapsDependencies,
      relativesViewSettings.objectiveDisplayMode,
      dispatch,
    ],
  );

  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);

  // Accessible maps (all user maps)
  const accessibleMaps = useMemo(() => {
    return getAccessibleMaps(userProfile);
  }, [userProfile]);

  // Related maps: only use allRelatedMilestoneMaps since added maps are handled separately
  const relatedMaps = useMemo(() => {
    return allRelatedMilestoneMaps;
  }, [allRelatedMilestoneMaps]);

  // In add mode, filter accessible maps (not already related) by search text
  const relatedMapUuids = useMemo(() => new Set(relatedMaps.map((m) => m.uuid)), [relatedMaps]);

  // Filtered dependencies (for filter mode) - apply text search to relevant dependencies
  const filteredDependencies = useMemo(() => {
    return relevantDependencies.filter((dep) => {
      // Apply text search filter
      const matchesSearch =
        !searchText ||
        dep.sourceResult?.text?.toLowerCase().includes(searchText.toLowerCase()) ||
        dep.targetResult?.text?.toLowerCase().includes(searchText.toLowerCase());

      return matchesSearch;
    });
  }, [relevantDependencies, searchText]);

  // Initialize visibility state when data loads
  useEffect(() => {
    if (!mainMapUuid || !allRelatedMilestoneMaps.length) return;

    // Get existing related maps from Redux
    const existingRelatedMapUuids = new Set(
      relativesViewSettings.relatedToVisibleMaps.map((m) => m.mapUuid),
    );

    // Find newly discovered related maps that aren't in Redux yet
    const newlyDiscoveredMaps = allRelatedMilestoneMaps.filter(
      (map) => !existingRelatedMapUuids.has(map.uuid),
    );

    // Only update if there are new maps to add
    if (newlyDiscoveredMaps.length > 0) {
      // Add new maps to existing ones (preserve existing visibility choices).
      // We're being careful to not reset the list here because that will cause
      // visible related maps to disappear.
      const newRelatedMaps = [
        ...relativesViewSettings.relatedToVisibleMaps, // Keep existing
        ...newlyDiscoveredMaps.map((map) => ({
          mapUuid: map.uuid,
          visible: true, // Show all related maps by default (matches production behavior)
        })),
      ];
      dispatch(setRelatedToVisibleMaps(newRelatedMaps));
    }

    // Initialize visibleMapsDependencies
    const newDependencyVisibility = allRelatedMapDependencies.map((dep) => {
      const depId = getDependencyId(dep);

      // Helper to check if a map is visible
      const isMapVisible = (mapUuid: string) => {
        return (
          mapUuid === mainMapUuid || // Is main map
          relativesViewSettings.addedMaps.find((m) => m.mapUuid === mapUuid)?.visible === true || // Is visible added map
          relativesViewSettings.relatedToVisibleMaps.find((m) => m.mapUuid === mapUuid)?.visible ===
            true // Is visible related map
        );
      };

      // Check if both source and target maps are visible
      const sourceMapVisible = isMapVisible(dep.sourceMap.uuid);
      const targetMapVisible = isMapVisible(dep.targetMap.uuid);

      // For intra-map dependencies (same source and target map), only need one map to be visible
      const isIntraMapDependency = dep.sourceMap.uuid === dep.targetMap.uuid;
      const shouldBeVisible = isIntraMapDependency
        ? sourceMapVisible || targetMapVisible // Intra-map: either is fine (they're the same)
        : sourceMapVisible && targetMapVisible; // Cross-map: both must be visible

      return {
        depId,
        visible: shouldBeVisible,
      };
    });
    dispatch(setVisibleMapsDependencies(newDependencyVisibility));

    setIsInitialized(true);
    // Warning: don't add these to the dependency list even if IDE tells you to:
    //  relativesViewSettings.addedMaps,
    //  relativesViewSettings.relatedToVisibleMaps
    // This is intentional to avoid infinite re-renders when visibility state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainMapUuid, allRelatedMilestoneMaps, allRelatedMapDependencies, dispatch]);

  // Callbacks
  const handleModeChange = useCallback((addMode: boolean) => setIsAddMode(addMode), []);
  const handleSearchTextChange = useCallback((text: string) => setSearchText(text), []);

  // Toggle map visibility in Redux
  const handleToggleMap = useCallback(
    (uuid: string) => {
      // Disallow toggling off the main map
      if (uuid === mainMapUuid) {
        return;
      }
      // Check if the map is in addedMaps or relatedToVisibleMaps
      const isAddedMap = relativesViewSettings.addedMaps.some((m) => m.mapUuid === uuid);
      const isRelatedMap = relativesViewSettings.relatedToVisibleMaps.some(
        (m) => m.mapUuid === uuid,
      );

      let updatedAddedMaps = relativesViewSettings.addedMaps;
      let updatedRelatedMaps = relativesViewSettings.relatedToVisibleMaps;

      if (isAddedMap) {
        // Toggle visibility in addedMaps
        updatedAddedMaps = relativesViewSettings.addedMaps.map((m) =>
          m.mapUuid === uuid ? { ...m, visible: !m.visible } : m,
        );
        dispatch(setAddedMaps(updatedAddedMaps));
      } else if (isRelatedMap) {
        // Toggle visibility in relatedToVisibleMaps
        updatedRelatedMaps = relativesViewSettings.relatedToVisibleMaps.map((m) =>
          m.mapUuid === uuid ? { ...m, visible: !m.visible } : m,
        );
        dispatch(setRelatedToVisibleMaps(updatedRelatedMaps));
      }

      // Update dependency visibility based on NEW map visibility (use updated values)
      const visibleMapUuids = new Set([
        ...updatedAddedMaps.filter((entry) => entry.visible).map((entry) => entry.mapUuid),
        ...updatedRelatedMaps.filter((entry) => entry.visible).map((entry) => entry.mapUuid),
      ]);

      const updatedDependencyVisibility = allRelatedMapDependencies.map((dep) => {
        const sourceMapVisible = visibleMapUuids.has(dep.sourceMap.uuid);
        const targetMapVisible = visibleMapUuids.has(dep.targetMap.uuid);
        return {
          depId: getDependencyId(dep),
          visible: sourceMapVisible && targetMapVisible, // Only show if both maps are visible
        };
      });
      dispatch(setVisibleMapsDependencies(updatedDependencyVisibility));
    },
    [
      dispatch,
      relativesViewSettings.addedMaps,
      relativesViewSettings.relatedToVisibleMaps,
      allRelatedMapDependencies,
      mainMapUuid,
    ],
  );

  // Toggle dependency visibility in Redux
  const handleToggleDependency = useCallback(
    (dep: any) => {
      const depId = getDependencyId(dep);
      const currentDep = relativesViewSettings.visibleMapsDependencies.find(
        (d) => d.depId === depId,
      );
      const isCurrentlyVisible = currentDep?.visible || false;

      // Check if we need to auto-downgrade from "show_all_relatives" mode
      if (
        relativesViewSettings.objectiveDisplayMode === 'show_all_relatives' &&
        isCurrentlyVisible
      ) {
        // If user is manually hiding a dependency, downgrade to "show_with_dependencies"
        dispatch(setObjectiveDisplayMode('show_with_dependencies'));
      }

      // Toggle the dependency visibility
      const updatedDependencies = relativesViewSettings.visibleMapsDependencies.map((d) =>
        d.depId === depId ? { ...d, visible: !d.visible } : d,
      );
      dispatch(setVisibleMapsDependencies(updatedDependencies));

      // If we're making the dependency visible, ensure both source and target maps are visible
      if (!isCurrentlyVisible) {
        let updatedAddedMaps = relativesViewSettings.addedMaps;
        let updatedRelatedMaps = relativesViewSettings.relatedToVisibleMaps;
        let mapsChanged = false;

        // Check if source map needs to be made visible
        const sourceMapInAdded = relativesViewSettings.addedMaps.find(
          (m) => m.mapUuid === dep.sourceMap.uuid,
        );
        const sourceMapInRelated = relativesViewSettings.relatedToVisibleMaps.find(
          (m) => m.mapUuid === dep.sourceMap.uuid,
        );

        if (sourceMapInAdded && !sourceMapInAdded.visible) {
          updatedAddedMaps = updatedAddedMaps.map((m) =>
            m.mapUuid === dep.sourceMap.uuid ? { ...m, visible: true } : m,
          );
          mapsChanged = true;
        } else if (sourceMapInRelated && !sourceMapInRelated.visible) {
          updatedRelatedMaps = updatedRelatedMaps.map((m) =>
            m.mapUuid === dep.sourceMap.uuid ? { ...m, visible: true } : m,
          );
          mapsChanged = true;
        }

        // Check if target map needs to be made visible
        const targetMapInAdded = updatedAddedMaps.find((m) => m.mapUuid === dep.targetMap.uuid);
        const targetMapInRelated = updatedRelatedMaps.find((m) => m.mapUuid === dep.targetMap.uuid);

        if (targetMapInAdded && !targetMapInAdded.visible) {
          updatedAddedMaps = updatedAddedMaps.map((m) =>
            m.mapUuid === dep.targetMap.uuid ? { ...m, visible: true } : m,
          );
          mapsChanged = true;
        } else if (targetMapInRelated && !targetMapInRelated.visible) {
          updatedRelatedMaps = updatedRelatedMaps.map((m) =>
            m.mapUuid === dep.targetMap.uuid ? { ...m, visible: true } : m,
          );
          mapsChanged = true;
        }

        // Dispatch map visibility updates if any maps were changed
        if (mapsChanged) {
          if (updatedAddedMaps !== relativesViewSettings.addedMaps) {
            dispatch(setAddedMaps(updatedAddedMaps));
          }
          if (updatedRelatedMaps !== relativesViewSettings.relatedToVisibleMaps) {
            dispatch(setRelatedToVisibleMaps(updatedRelatedMaps));
          }
        }
      }
    },
    [
      dispatch,
      relativesViewSettings.visibleMapsDependencies,
      relativesViewSettings.addedMaps,
      relativesViewSettings.relatedToVisibleMaps,
      relativesViewSettings.objectiveDisplayMode,
    ],
  );

  // Bulk update dependencies visibility - for Select All functionality
  const handleBulkUpdateDependencies = useCallback(
    (updatedDependencies: Array<{ depId: string; visible: boolean }>) => {
      // Check if we need to auto-downgrade from "show_all_relatives" mode
      if (relativesViewSettings.objectiveDisplayMode === 'show_all_relatives') {
        // If user is bulk-hiding dependencies, downgrade to "show_with_dependencies"
        const anyBeingHidden = updatedDependencies.some((dep) => {
          const currentDep = relativesViewSettings.visibleMapsDependencies.find(
            (d) => d.depId === dep.depId,
          );
          return currentDep?.visible && !dep.visible;
        });

        if (anyBeingHidden) {
          dispatch(setObjectiveDisplayMode('show_with_dependencies'));
        }
      }

      // Single batch update to Redux - all or nothing
      dispatch(setVisibleMapsDependencies(updatedDependencies));
    },
    [
      dispatch,
      relativesViewSettings.objectiveDisplayMode,
      relativesViewSettings.visibleMapsDependencies,
    ],
  );

  // Add a map to related maps in Redux
  const handleAddMap = useCallback(
    async (map: any) => {
      try {
        // First load the map data
        setLoadingMapUuids((prev) => new Set([...Array.from(prev), map.uuid]));
        const result = await dispatch(loadMapDataAsync(map.uuid)).unwrap();

        // Only update visibility if the map was successfully loaded
        if (result.milestoneMap) {
          // Add new map as visible if not already present
          const isAlreadyAdded = relativesViewSettings.addedMaps.some(
            (entry) => entry.mapUuid === map.uuid,
          );
          const updated = isAlreadyAdded
            ? relativesViewSettings.addedMaps
            : [...relativesViewSettings.addedMaps, { mapUuid: map.uuid, visible: true }];
          dispatch(setAddedMaps(updated));
          setSearchText('');
        }
      } catch (error) {
        console.error('Failed to load map data:', error);
      } finally {
        setLoadingMapUuids((prev) => {
          const next = new Set(prev);
          next.delete(map.uuid);
          return next;
        });
      }
    },
    [dispatch, relativesViewSettings.addedMaps, setSearchText],
  );

  // Don't render until initialized
  if (!isInitialized) {
    return null;
  }

  return (
    <DropdownMenu
      aria-label="Map options menu"
      className="w-80 min-w-80 max-w-80"
      style={{ width: 320, minWidth: 320, maxWidth: 320 }}
    >
      <DropdownInputForm
        isAddMode={isAddMode}
        searchText={searchText}
        onModeChange={handleModeChange}
        onSearchTextChange={handleSearchTextChange}
        accessibleMaps={accessibleMaps}
        relatedMapUuids={relatedMapUuids}
        onAddMap={handleAddMap}
        disabled={props.disabled}
      />
      <DropdownDivider />
      <DropdownList
        isAddMode={isAddMode}
        addedMaps={relativesViewSettings.addedMaps}
        relatedToVisibleMaps={relativesViewSettings.relatedToVisibleMaps}
        dependencies={filteredDependencies}
        onToggleMap={handleToggleMap}
        onToggleDependency={handleToggleDependency}
        onToggleMapDependencies={handleToggleMapDependencies}
        onBulkUpdateDependencies={handleBulkUpdateDependencies}
        searchText={searchText}
        disabled={props.disabled}
        loadingMapUuids={loadingMapUuids}
      />
    </DropdownMenu>
  );
}
